import React, { useMemo } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import SkeletonPlaceholder from 'react-native-skeleton-placeholder';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { GTFonts, useLayoutSize, useTheme, type Theme } from '../../../theme';
import {
  fs,
  formatMoney,
  formatWithCommas,
  iconSize,
  s,
  v,
} from '../../../utils/libs';
import Button from '../../Reusables/Button';
import { ComparisonChip } from '../../Reusables/ComparisonChip';
import type { ExpenseSummary } from '../../../api/backofficeExpenses';

type ExpenseHeroCardProps = {
  summary: ExpenseSummary | null;
  /** First load: the figures show as skeletons. */
  loading: boolean;
  /** Message of the last failed load. Figures then show "—" with Retry, never zeros. */
  error?: string | null;
  dateRangeLabel: string;
  currencyText: string;
  /** e.g. "last month", shown after the comparison percentage. */
  comparisonPeriodLabel: string;
  onViewExpenses: () => void;
  onAddExpense: () => void;
  onRetry: () => void;
};

/**
 * The period's total spend, how it compares with the previous period, and
 * Add Expense. Laid out like the dashboard's Money Collected card; the button
 * follows New Sale on Customer Details (full width on phones, top right with
 * the sidebar layout).
 */
const ExpenseHeroCard: React.FC<ExpenseHeroCardProps> = ({
  summary,
  loading,
  error,
  dateRangeLabel,
  currencyText,
  comparisonPeriodLabel,
  onViewExpenses,
  onAddExpense,
  onRetry,
}) => {
  const { theme } = useTheme();
  const { useSidebarNav } = useLayoutSize();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const failed = Boolean(error);
  const figures = failed ? null : summary;
  const previous = figures?.previousPeriod;
  const comparison =
    figures && previous && previous.changeDirection !== 'flat'
      ? {
          change:
            Number(figures.totalExpenses || 0) -
            Number(previous.totalExpenses || 0),
          percentageChange:
            (previous.changeDirection === 'down' ? -1 : 1) *
            Number(previous.changePercent || 0),
        }
      : null;

  const stats = [
    { label: 'Manual entries', value: figures?.manualCount },
    { label: 'Auto-recorded', value: figures?.autoRecordedCount },
    { label: 'Categories', value: figures?.activeCategoriesCount },
  ];

  const renderSkeleton = (
    width: number,
    height: number,
    color: string = theme.colors.border,
  ) => (
    <SkeletonPlaceholder
      backgroundColor={color}
      highlightColor={theme.colors.surfaceSecondary}>
      <SkeletonPlaceholder.Item
        width={width}
        height={height}
        borderRadius={s(4)}
      />
    </SkeletonPlaceholder>
  );

  const viewAllLink = (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel="View all expenses"
      onPress={onViewExpenses}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      style={styles.viewAllLink}>
      <Text style={styles.viewAllText}>View all</Text>
      <MaterialCommunityIcons
        name="chevron-right"
        size={iconSize(16)}
        color={theme.colors.successEmerald}
      />
    </TouchableOpacity>
  );

  const addExpenseButton = (
    <Button
      text="Add Expense"
      icon="plus-circle"
      trailingIcon="chevron-right"
      fullWidth={!useSidebarNav}
      height={v(useSidebarNav ? 44 : 48)}
      fontSize={useSidebarNav ? 15 : 16}
      paddingHorizontal={useSidebarNav ? s(20) : undefined}
      borderRadius={s(theme.borderRadius.md)}
      backgroundColor={theme.colors.successEmerald}
      rippleColor={theme.colors.successEmeraldDark}
      onPress={onAddExpense}
    />
  );

  return (
    <View style={styles.card}>
      <View style={styles.headerRow}>
        <View style={styles.titleBlock}>
          <View style={styles.titleRow}>
            <MaterialCommunityIcons
              name="cash-plus"
              size={fs(16)}
              color={theme.colors.error}
            />
            <Text style={styles.title}>Expenses</Text>
          </View>
          <Text style={styles.periodLabel}>{dateRangeLabel}</Text>
        </View>
        {useSidebarNav ? (
          <View style={styles.headerActions}>
            {viewAllLink}
            {addExpenseButton}
          </View>
        ) : (
          viewAllLink
        )}
      </View>

      {loading ? (
        <View style={styles.amountSkeleton}>{renderSkeleton(s(150), v(30))}</View>
      ) : (
        <Text style={styles.amount} numberOfLines={1} adjustsFontSizeToFit>
          {figures ? `${currencyText} ${formatMoney(figures.totalExpenses)}` : '—'}
        </Text>
      )}

      {loading ? (
        <View style={styles.comparisonSkeleton}>
          {renderSkeleton(s(190), v(14))}
        </View>
      ) : comparison && previous ? (
        <View style={styles.comparisonRow}>
          <ComparisonChip
            comparison={comparison}
            periodLabel={comparisonPeriodLabel}
            increaseIsGood={false}
          />
          <Text style={styles.previousAmount}>
            ({currencyText} {formatMoney(previous.totalExpenses)})
          </Text>
        </View>
      ) : null}

      <View style={styles.statStrip}>
        {stats.map((stat, index) => (
          <View
            key={stat.label}
            style={[styles.statCell, index > 0 && styles.statCellDivider]}>
            <Text style={styles.statLabel}>{stat.label}</Text>
            {loading ? (
              renderSkeleton(s(24), v(18), theme.colors.borderGray200)
            ) : (
              <Text style={styles.statValue}>
                {figures ? formatWithCommas(Number(stat.value || 0), 0) : '—'}
              </Text>
            )}
          </View>
        ))}
      </View>

      {failed ? (
        <View style={styles.errorRow} accessibilityRole="alert">
          <MaterialCommunityIcons
            name="alert-circle-outline"
            size={iconSize(16)}
            color={theme.colors.errorDark}
          />
          <Text style={styles.errorText} numberOfLines={2}>
            {error}
          </Text>
          <Pressable
            onPress={onRetry}
            accessibilityRole="button"
            accessibilityLabel="Retry loading expenses"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={({ pressed }) => [styles.retry, pressed && styles.pressed]}>
            <Text style={styles.retryText}>Retry</Text>
          </Pressable>
        </View>
      ) : null}

      {useSidebarNav ? null : <View style={styles.cta}>{addExpenseButton}</View>}
    </View>
  );
};

export default ExpenseHeroCard;

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(16),
      paddingTop: v(14),
      paddingBottom: v(16),
      ...theme.shadows.sm,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      columnGap: s(12),
    },
    titleBlock: {
      flex: 1,
      minWidth: 0,
      rowGap: v(2),
    },
    // Icon before the title, as on Money Collected and Still Owed To You
    titleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: s(6),
    },
    title: {
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: fs(16),
      lineHeight: fs(20),
    },
    periodLabel: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
    headerActions: {
      flexShrink: 0,
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: s(16),
    },
    viewAllLink: {
      flexShrink: 0,
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: v(2),
    },
    viewAllText: {
      color: theme.colors.successEmerald,
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(16),
      textDecorationLine: 'underline',
    },
    amount: {
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: fs(28),
      lineHeight: fs(34),
      marginTop: v(10),
    },
    amountSkeleton: {
      marginTop: v(12),
    },
    comparisonRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      columnGap: s(4),
      marginTop: v(4),
    },
    comparisonSkeleton: {
      marginTop: v(8),
    },
    previousAmount: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
    statStrip: {
      flexDirection: 'row',
      marginTop: v(14),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surfaceSecondary,
    },
    // Labels may wrap on narrow phones; the values still share one baseline.
    statCell: {
      flex: 1,
      minWidth: 0,
      justifyContent: 'space-between',
      rowGap: v(2),
      paddingVertical: v(10),
      paddingHorizontal: s(10),
    },
    statCellDivider: {
      borderLeftWidth: 1,
      borderLeftColor: theme.colors.borderGray200,
    },
    statLabel: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
    statValue: {
      color: theme.colors.text,
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(20),
    },
    // Same banner as the Customer Details summary when it fails to load
    errorRow: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: s(8),
      marginTop: v(12),
      paddingVertical: v(10),
      paddingHorizontal: s(12),
      borderRadius: theme.borderRadius.md,
      borderWidth: 1,
      borderColor: theme.colors.errorLight,
      backgroundColor: theme.colors.errorSurface,
    },
    errorText: {
      flex: 1,
      color: theme.colors.errorDark,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
    retry: {
      minWidth: s(44),
      minHeight: v(28),
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: s(10),
      borderRadius: theme.borderRadius.round,
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as any,
    retryText: {
      color: theme.colors.errorDark,
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
    pressed: {
      opacity: 0.7,
    },
    cta: {
      marginTop: v(14),
    },
  });
